/** 产品新建/编辑独立页面：/products/new 与 /products/:id */
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button, EmptyState, PageHeader, Spinner } from "@/components/ui";
import { useProducts } from "./hooks";
import { ProductWizard } from "./wizard/ProductWizard";
import { DescribeStep } from "./wizard/DescribeStep";
import type { WizardDraft } from "./wizard/serializers";

export function ProductEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const scope = params.get("scope") ?? undefined;
  const isNew = !id;

  // 新建时先走"描述→AI解析"，解析完(或选手动)再进向导
  const [mode, setMode] = useState<"describe" | "wizard">(isNew ? "describe" : "wizard");
  const [aiDraft, setAiDraft] = useState<WizardDraft | undefined>();
  const [aiNotes, setAiNotes] = useState<string[]>([]);

  const { data, isLoading } = useProducts(scope);
  const products = data?.data;
  const product = isNew ? undefined : products?.find((p) => String(p.id) === id);

  const backTo = `/products${scope ? `?scope=${scope}` : ""}`;
  const close = () => navigate(backTo);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Spinner /> 加载中…
      </div>
    );
  }

  if (!isNew && !product) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <EmptyState
          title="产品不存在或已被删除"
          action={<Button variant="outline" icon={<ArrowLeft />} onClick={close}>返回产品列表</Button>}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title={isNew ? "新建产品" : product!.name}
        leading={
          <Button variant="ghost" size="icon-sm" onClick={close} aria-label="返回产品列表" title="返回产品列表" className="-ml-1.5">
            <ArrowLeft />
          </Button>
        }
      />

      {mode === "describe" ? (
        <DescribeStep
          onParsed={(draft, notes) => {
            setAiDraft(draft);
            setAiNotes(notes);
            setMode("wizard");
          }}
          onManual={() => setMode("wizard")}
        />
      ) : (
        <ProductWizard
          key={product?.id ?? (aiDraft ? "ai" : "new")}
          product={product}
          products={isNew ? products : undefined}
          scope={scope}
          initialDraft={aiDraft}
          aiNotes={aiNotes}
          onClose={close}
          onBack={isNew ? () => setMode("describe") : undefined}
        />
      )}
    </div>
  );
}
